import { PROFILE_LABEL_SX } from "../profileFieldStyles";
import ContentEditorFields from "./ContentEditorFields";
import ContentNotesPanel from "./ContentNotesPanel";
import { narrativeEditorHtml } from "../../utils/narrativeHtml";
import React, { useState } from "react";
import { Alert, Avatar, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from "@mui/material";
import { EditOutlined as EditOutlinedIcon } from "../icons/FigmaControlIcons";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import Breadcrumb from "../Breadcrumb";
import ProfileHeader from "../TabsHeader";
import ListTableTabsTemplate from "../layouts/ListTableTabsTemplate";
import QuickActionsCard from "../sidebar-widgets/QuickActionsCard";
import { TAB_VIEW_CONTENT_WRAPPER_SX, TAB_VIEW_HEADER_SX } from "../tabViewContentSpacing";
import { toolbarPillButtonSx } from "../../utils/pillButtonStyles";
import { mutateContentLibrary } from "../../utils/contentApi";
import { buildPublicSharePayload, createAndCopyPublicShareLink, SHARE_LINK_COPIED_MESSAGE } from "../../utils/publicShareLinks";

export const CONTENT_DETAIL_TABS = [
  { value: "details", label: "Details" },
  { value: "content", label: "Content" },
  { value: "notes", label: "Notes" },
];

function Field({ label, children }) {
  return <Box sx={{ minWidth: 0 }}>
    <Typography className="profile-field-label" sx={{ ...PROFILE_LABEL_SX, mb: 2 }}>{label}</Typography>
    <Box sx={{ border: "1px solid #D2D8DE", borderRadius: "8px", px: 2, py: 1, minHeight: 42, fontSize: 14, lineHeight: "24px", overflowWrap: "anywhere", color: "#344054" }}>{children || "—"}</Box>
  </Box>;
}

export default function ContentDetailsView({ row, categoryLabel, tab, onTabChange, onBack, onUpdated, statusOptions, sources = [] }) {
  const [editor, setEditor] = useState(null);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const openEditor = (kind) => {
    setError("");
    setDraft({ content_type_key: row.content_type_key, title: row.content_name, description: row.description || "", owner_name: row.owner_name === "Unassigned" ? "" : row.owner_name,
      related_entity_label: row.related_entity_label || "", source: row.source || "", tags: (row.tags || []).join(", "),
      note_title: row.note_title || "Note", note_body: row.note_body || "" });
    setEditor(kind);
  };
  const save = async (changes) => {
    setBusy(true); setError(""); setMessage("");
    try {
      await mutateContentLibrary(row.content_type_key, [row.id], "update", changes);
      onUpdated(changes);
      setEditor(null);
      setMessage("Changes saved.");
    } catch (err) { setError(err?.message || "Unable to save changes. Please try again."); }
    finally { setBusy(false); }
  };
  const share = async () => {
    setBusy(true); setError(""); setMessage("");
    try {
      await createAndCopyPublicShareLink({ apiBase: (process.env.REACT_APP_API_BASE || "").replace(/\/$/, ""), payload: buildPublicSharePayload("content", row) });
      setMessage(SHARE_LINK_COPIED_MESSAGE);
    } catch (err) { setError(err?.message || "Unable to share content."); }
    finally { setBusy(false); }
  };
  const input = (name, label, props = {}) => <TextField fullWidth label={label} value={draft[name] || ""} onChange={(event) => setDraft((prev) => ({ ...prev, [name]: event.target.value }))} disabled={busy} {...props} />;
  const statuses = Array.from(new Set([row.status, ...statusOptions.map((option) => option.value)]));
  return <>
    <ListTableTabsTemplate
      breadcrumb={<Breadcrumb items={[{ label: "Overview", path: "/overview" }, { label: "Content", path: "/content" }, { label: categoryLabel, onClick: onBack }, { label: row.content_name }]} />}
      leftColumnSx={{ border: "1px solid #D0D5DD", borderRadius: 2, pt: 3 }}
      headerSection={<ProfileHeader title={row.content_name} tab={tab} onTabChange={onTabChange} tabs={CONTENT_DETAIL_TABS}
        metaLabel="Owner" metaValue={row.owner_name} actionLabel="Status" actionValue={row.status} actionDisabled={busy}
        actionShowPlaceholderOption={false} actionOptions={statuses.map((value) => ({ value, label: value }))}
        onActionChange={(status) => save({ status })} sx={TAB_VIEW_HEADER_SX} />}
      rightSidebar={<Stack spacing={2}><QuickActionsCard actionItems={[
        { key: "edit", label: "Edit Content", icon: <EditOutlinedIcon />, onClick: () => openEditor("content"), disabled: busy },
        { key: "share", label: "Share", icon: <ShareOutlinedIcon />, onClick: share, disabled: busy },
        { key: "notes", label: row.note_body ? "Edit Note" : "Add Note", icon: <NotesOutlinedIcon />, onClick: () => openEditor("notes"), disabled: busy },
      ]} /><ContentNotesPanel sidebar row={row} disabled={busy} onAdd={() => openEditor("notes")} onEdit={() => openEditor("notes")} onViewAll={() => onTabChange("notes")} /></Stack>}
      contentSection={<Box sx={TAB_VIEW_CONTENT_WRAPPER_SX}>
        {error && !editor && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {message && <Alert severity="success" onClose={() => setMessage("")} sx={{ mb: 2 }}>{message}</Alert>}
        {tab !== "notes" && <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
          <Typography className="profile-section-title" component="h2" sx={{ color: "#3451F4", fontSize: 20, fontWeight: 700 }}>{tab === "details" ? "Summary" : tab === "notes" ? "Notes" : row.content_type_label}</Typography>
          <Button disabled={busy} onClick={() => openEditor(tab === "notes" ? "notes" : "content")}>Edit</Button>
        </Stack>}
        {tab === "details" && <>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 4 }}>
            <Field label="Content Type">{row.content_type_label}</Field>
            <Field label="Owner"><Stack direction="row" spacing={1} alignItems="center"><Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: "#E0F2FE", color: "#3451F4" }}>{row.owner_initials}</Avatar><span>{row.owner_name}</span></Stack></Field>
            <Field label="Related Entity">{row.related_entity_label}</Field><Field label="Source">{row.source}</Field>
            <Field label="Usage">{row.usage_label}</Field><Field label={row.performance_label}>{row.performance_display}</Field>
            <Field label="Last Used">{row.last_used_label}</Field>
            <Field label="Tags">{row.tags?.length ? <Stack direction="row" gap={1} flexWrap="wrap">{row.tags.map((tag, index) => <Chip key={`${tag}-${index}`} size="small" label={tag} sx={{ bgcolor: "#F4EBFF", color: "#7C3AED" }} />)}</Stack> : "No tags"}</Field>
          </Box>
        </>}
        {tab === "content" && <Box sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", border: "1px solid #D0D5DD", borderRadius: 1, p: 2, minHeight: 200 }} dangerouslySetInnerHTML={{ __html: narrativeEditorHtml(row.description || "No content yet. Select Edit to add content.") }} />}
        {tab === "notes" && <ContentNotesPanel row={row} disabled={busy} onAdd={() => openEditor("notes")} onEdit={() => openEditor("notes")} onViewAll={() => onTabChange("notes")} />}
      </Box>}
    />
    <Dialog open={Boolean(editor)} onClose={() => !busy && setEditor(null)} fullWidth maxWidth="md">
      <DialogTitle data-popup-header>{editor === "notes" ? "Edit Note" : `Edit ${row.content_type_label}`}</DialogTitle>
      <DialogContent><Stack spacing={2.5} sx={{ pt: 1 }}>
        {error && <Alert severity="error">{error}</Alert>}
        {editor === "notes" ? <>{input("note_title", "Note Title", { inputProps: { maxLength: 255 } })}{input("note_body", "Note", { multiline: true, minRows: 6 })}</> : <>
          <ContentEditorFields draft={draft} onChange={setDraft} disabled={busy} sources={sources} />
        </>}
      </Stack></DialogContent>
      <DialogActions data-popup-footer><Button disabled={busy} onClick={() => setEditor(null)}>Cancel</Button>
        <Button className="pill" sx={toolbarPillButtonSx} disabled={busy || (editor === "content" && !draft.title?.trim())} onClick={() => save(editor === "notes"
          ? { note_title: draft.note_title.trim(), note_body: draft.note_body }
          : { title: draft.title.trim(), description: draft.description, owner_name: draft.owner_name.trim(), related_entity_label: draft.related_entity_label.trim(), source: draft.source.trim(), tags: [...new Set(draft.tags.split(",").map((tag) => tag.trim()).filter(Boolean))] })}>{busy ? "Saving…" : "Save"}</Button>
      </DialogActions>
    </Dialog>
  </>;
}
